'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { isAxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { submitOriginalPlusApplicationReq } from '@/features/original-plus/api/submit-original-plus-application';
import {
  applicationFormConsultationLevelId,
  applicationFormLevelLabels,
  authorLevels,
  getApplicationFormParticipationDescription,
  type ApplicationFormLevelId,
} from '@/features/original-plus/constants';
import {
  originalPlusApplicationSchema,
  type OriginalPlusApplicationFormValues,
} from '@/features/original-plus/components/OriginalPlusApplicationForm/application-form.schema';
import { useAuthorLevelsApplication } from '@/features/original-plus/context/use-author-levels-application';
import { showWarningToast } from '@/components/ui/sonner';
import { sendYandexMetrica } from '@/utils/send-metrika-yandex.utils';

export function useOriginalPlusApplicationForm() {
  const { selectedLevelId, setSelectedLevelId, wantsConsultation, setWantsConsultation } =
    useAuthorLevelsApplication();

  const [successOpen, setSuccessOpen] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const formLevelId: ApplicationFormLevelId = wantsConsultation
    ? applicationFormConsultationLevelId
    : selectedLevelId;

  const form = useForm<OriginalPlusApplicationFormValues>({
    resolver: zodResolver(originalPlusApplicationSchema),
    defaultValues: {
      pseudonym: '',
      contact: '',
      workLink: '',
      levelId: formLevelId,
      personalDataAccepted: false,
      rightsConfirmed: false,
      rulesAccepted: false,
    },
  });

  useEffect(() => {
    form.setValue('levelId', formLevelId);
  }, [form, formLevelId]);

  const handleLevelChange = (levelId: ApplicationFormLevelId) => {
    if (levelId === applicationFormConsultationLevelId) {
      setWantsConsultation(true);
      return;
    }

    setSelectedLevelId(levelId);
  };

  const resetForm = () => {
    form.reset({
      pseudonym: '',
      contact: '',
      workLink: '',
      levelId: authorLevels[0].id,
      personalDataAccepted: false,
      rightsConfirmed: false,
      rulesAccepted: false,
    });
    setWantsConsultation(false);
    setSubmitError(null);
  };

  const onSubmit = form.handleSubmit(async (values) => {
    setSubmitError(null);

    const participationDescription = getApplicationFormParticipationDescription(values.levelId);

    try {
      await submitOriginalPlusApplicationReq({
        publicationName: values.pseudonym,
        contact: values.contact,
        workTitle: values.workLink,
        description: participationDescription,
      });

      sendYandexMetrica({ params: 'original_plus-form-sent' });
      setSuccessOpen(true);
    } catch (error) {
      console.error('Original Plus application submit error:', error);

      const message = isAxiosError(error)
        ? typeof error.response?.data?.message === 'string'
          ? error.response.data.message
          : null
        : null;

      const fallbackMessage = 'Не удалось отправить заявку. Попробуйте ещё раз.';
      setSubmitError(message ?? fallbackMessage);
      showWarningToast(message ?? fallbackMessage);
    }
  });

  const handleSuccessOpenChange = (open: boolean) => {
    setSuccessOpen(open);

    if (!open) {
      resetForm();
    }
  };

  return {
    form,
    applicationFormLevelLabels,
    authorLevels,
    isSubmitting: form.formState.isSubmitting,
    submitError,
    successOpen,
    handleLevelChange,
    onSubmit,
    handleSuccessOpenChange,
  };
}
